import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import Avatar from "../components/Avatar";
import TrendCard from "../components/TrendCard";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { acceptDuel, getDuelByCode, getTrendIndexes } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Swords, Crown, Shield, X, Trophy, Info, Copy, Share2, MessageCircle } from "lucide-react";

const CATS = ["all", "bollywood", "reality", "creator", "music", "athletes"];

export default function DuelView() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [duel, setDuel] = useState(null);
  const [indexes, setIndexes] = useState([]);
  const [cat, setCat] = useState("all");
  const [picks, setPicks] = useState([]);
  const [captain, setCaptain] = useState(null);
  const [vc, setVc] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => getDuelByCode(code).then(setDuel).catch(() => toast.error("Duel not found"));

  useEffect(() => {
    load();
    getTrendIndexes().then(setIndexes);
  }, [code]); // eslint-disable-line

  const filtered = useMemo(
    () => (cat === "all" ? indexes : indexes.filter(x => x.category === cat)),
    [indexes, cat]
  );

  if (!duel) {
    return (
      <div className="min-h-screen pb-safe" data-testid="duel-view-loading">
        <Header title="Duel" />
        <div className="max-w-xl mx-auto px-4 pt-6 text-white/60">Loading duel…</div>
        <BottomNav />
      </div>
    );
  }

  const isCreator = user && user.user_id === duel.creator_id;
  const isOpponent = user && user.user_id === duel.opponent_id;
  const isDone = duel.status === "done";
  const shareUrl = `${window.location.origin}/duel/${duel.invite_code}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  };
  const share = async () => {
    const msg = `I just challenged you on Fanpuff 🔥 Beat my lineup (${duel.creator_lineup.points.toFixed(0)} pts): ${shareUrl}`;
    if (navigator.share) { try { await navigator.share({ title: "Fanpuff Duel", text: msg, url: shareUrl }); } catch {} }
    else { copy(); }
  };
  const whatsapp = () => {
    const msg = `I just challenged you on Fanpuff 🔥 Beat my lineup (${duel.creator_lineup.points.toFixed(0)} pts): ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const pickedSet = new Set(picks);
  const picksDetail = picks.map((pid) => indexes.find((x) => x.index_id === pid)).filter(Boolean);

  const togglePick = (idx) => {
    if (pickedSet.has(idx.index_id)) {
      setPicks(picks.filter((p) => p !== idx.index_id));
      if (captain === idx.index_id) setCaptain(null);
      if (vc === idx.index_id) setVc(null);
    } else {
      if (picks.length >= 6) { toast("Lineup full — remove to swap."); return; }
      setPicks([...picks, idx.index_id]);
    }
  };

  const accept = async () => {
    if (picks.length !== 6) return toast.error("Pick exactly 6 stars.");
    if (!captain || !vc) return toast.error("Crown a Captain and Vice-Captain.");
    if (captain === vc) return toast.error("Captain and VC must differ.");
    setSubmitting(true);
    try {
      const updated = await acceptDuel(code, {
        contest_id: "duel",
        picks,
        captain_id: captain,
        vice_captain_id: vc,
      });
      setDuel(updated);
      const iWon = updated.winner_id === user?.user_id;
      if (iWon) confetti({ particleCount: 200, spread: 100, origin: { y: 0.4 }, colors: ["#00FF66", "#FFD600", "#00F0FF"] });
      toast[iWon ? "success" : "message"](iWon ? "You won! +500 XP" : "So close. Rematch?");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept duel.");
    } finally { setSubmitting(false); }
  };

  // Lineup display helper
  const LineupStrip = ({ lineup, name, side, color, points }) => {
    if (!lineup) {
      return (
        <div className="glass rounded-2xl p-4 text-center">
          <p className="text-xs text-white/50 uppercase tracking-widest">{side}</p>
          <p className="font-display font-bold mt-1 text-white/40">Waiting for opponent…</p>
        </div>
      );
    }
    return (
      <div className="glass rounded-2xl p-3 relative overflow-hidden" data-testid={`duel-lineup-${side}`}>
        <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-30" style={{ background: `radial-gradient(circle,${color},transparent 70%)` }} />
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color }}>{side}</p>
            <p className="font-display font-bold truncate max-w-[160px]">{name}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-white/50 uppercase tracking-widest">Points</p>
            <p className="font-display text-2xl font-bold" style={{ color }}>{points.toFixed(0)}</p>
          </div>
        </div>
        <div className="grid grid-cols-6 gap-1.5 mt-3">
          {lineup.picks.map((pid) => {
            const idx = duel.index_map?.[pid];
            if (!idx) return <div key={pid} className="aspect-square rounded-md bg-white/5" />;
            const isC = lineup.captain_id === pid;
            const isV = lineup.vice_captain_id === pid;
            return (
              <div key={pid} className={`relative aspect-square rounded-md overflow-hidden ring-1 ${isC ? "ring-[#B026FF]" : isV ? "ring-[#00F0FF]" : "ring-white/10"}`}>
                <Avatar name={idx.name} category={idx.category} symbol={idx.symbol} size={80} rounded={0} className="!w-full !h-full !ring-0" />
                {(isC || isV) && (
                  <span className="absolute bottom-0 left-0 right-0 text-[8px] font-bold text-center py-0.5"
                    style={{ background: isC ? "#B026FF" : "#00F0FF", color: "#000" }}>
                    {isC ? "C 2x" : "VC 1.5x"}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const resultTag = () => {
    if (!isDone) return <span className="chip" style={{ background: "#FFD60022", color: "#FFD600", border: "1px solid #FFD60055" }}>● Open — waiting</span>;
    const youWon = user && duel.winner_id === user.user_id;
    const youLost = user && duel.winner_id && duel.winner_id !== user.user_id && (isCreator || isOpponent);
    if (youWon) return <span className="chip" style={{ background: "#00FF6622", color: "#00FF66", border: "1px solid #00FF6655" }}><Trophy className="w-3 h-3" /> You won!</span>;
    if (youLost) return <span className="chip" style={{ background: "#FF005522", color: "#FF0055", border: "1px solid #FF005555" }}>You lost</span>;
    return <span className="chip" style={{ background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}>Completed</span>;
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="duel-view-page">
      <Header title="Duel" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        <div className="glass-strong rounded-2xl p-4 relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-40" style={{ background: "radial-gradient(circle,#FF0055,transparent 70%)" }} />
          <div className="flex items-center gap-2">
            <Swords className="w-5 h-5 text-[#FF0055]" />
            <p className="text-xs uppercase tracking-widest font-bold text-white/60">1v1 Duel · <span className="font-mono-brand">{duel.invite_code}</span></p>
          </div>
          <h1 className="font-display text-2xl font-bold mt-1">{duel.creator_name} vs {duel.opponent_name || "You?"}</h1>
          <div className="mt-2">{resultTag()}</div>
        </div>

        <LineupStrip lineup={duel.creator_lineup} name={duel.creator_name} side="Creator" color="#B026FF" points={duel.creator_lineup.points} />
        <LineupStrip lineup={duel.opponent_lineup} name={duel.opponent_name || "—"} side="Opponent" color="#00F0FF" points={duel.opponent_lineup?.points || 0} />

        {/* Share CTA for creator when still open */}
        {isCreator && !isDone && (
          <div className="glass rounded-2xl p-4">
            <p className="text-xs uppercase tracking-widest font-bold text-white/50">Share your invite</p>
            <p className="text-[11px] text-white/60 mt-1 break-all">{shareUrl}</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <button onClick={copy} data-testid="duel-view-copy" className="glass rounded-xl py-2 inline-flex items-center justify-center gap-1 text-xs font-bold hover:bg-white/10">
                <Copy className="w-4 h-4" /> Copy
              </button>
              <button onClick={whatsapp} data-testid="duel-view-whatsapp" className="rounded-xl py-2 inline-flex items-center justify-center gap-1 text-xs font-bold text-black" style={{ background: "#00FF66" }}>
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
              <button onClick={share} data-testid="duel-view-share" className="rounded-xl py-2 inline-flex items-center justify-center gap-1 text-xs font-bold text-black" style={{ background: "linear-gradient(90deg,#FF0055,#B026FF)" }}>
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>
        )}

        {/* Opponent builder — only if duel still open AND user is NOT the creator */}
        {!isDone && !isCreator && (
          <>
            <div className="glass rounded-2xl p-4">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-[#00F0FF]" />
                <p className="text-xs uppercase tracking-widest font-bold text-white/60">Your turn</p>
              </div>
              <h2 className="font-display text-xl font-bold mt-1">Beat {duel.creator_name}'s <span style={{ color: "#B026FF" }}>{duel.creator_lineup.points.toFixed(0)} pts</span></h2>
              <p className="text-xs text-white/60 mt-1">Pick 6, crown 1. Higher points = win +500 XP. Instant result.</p>
            </div>

            <div className="glass-strong rounded-2xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-display font-semibold">Your Squad ({picks.length}/6)</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 6 }).map((_, i) => {
                  const idx = picksDetail[i];
                  if (!idx) return (
                    <div key={i} className="aspect-square rounded-xl border border-dashed border-white/15 grid place-items-center text-white/30 text-xs">
                      Slot {i + 1}
                    </div>
                  );
                  const isC = captain === idx.index_id;
                  const isV = vc === idx.index_id;
                  return (
                    <div key={idx.index_id} className={`relative rounded-xl overflow-hidden ring-1 ${isC ? "ring-[#B026FF]" : isV ? "ring-[#00F0FF]" : "ring-white/10"}`}>
                      <div className="w-full aspect-square">
                        <Avatar name={idx.name} category={idx.category} symbol={idx.symbol} size={140} rounded={0} className="!w-full !h-full !ring-0" />
                      </div>
                      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,transparent 40%, rgba(0,0,0,0.85))" }} />
                      <button onClick={() => togglePick(idx)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 grid place-items-center" data-testid={`duel-accept-remove-${idx.handle}`} aria-label="remove">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="absolute bottom-1 left-1 right-1">
                        <p className="text-[10px] font-bold truncate">{idx.name}</p>
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => setCaptain(captain === idx.index_id ? null : idx.index_id)} data-testid={`duel-accept-c-${idx.handle}`} className={`text-[9px] px-1.5 py-0.5 rounded ${isC ? "bg-[#B026FF] text-black font-bold" : "bg-white/10"}`}>C 2x</button>
                          <button onClick={() => setVc(vc === idx.index_id ? null : idx.index_id)} data-testid={`duel-accept-vc-${idx.handle}`} className={`text-[9px] px-1.5 py-0.5 rounded ${isV ? "bg-[#00F0FF] text-black font-bold" : "bg-white/10"}`}>VC 1.5x</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-3 text-[11px] text-white/50">
                <span className="inline-flex items-center gap-1"><Crown className="w-3 h-3 text-[#B026FF]" /> Captain 2×.</span>
                <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3 text-[#00F0FF]" /> VC 1.5×.</span>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto py-1">
              {CATS.map((c) => (
                <button
                  key={c}
                  data-testid={`duel-accept-filter-${c}`}
                  onClick={() => setCat(c)}
                  className="chip whitespace-nowrap px-3 py-1.5 text-xs"
                  style={cat === c
                    ? { background: "linear-gradient(90deg,#00F0FF,#B026FF)", color: "#000", border: "1px solid transparent" }
                    : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
                >{c}</button>
              ))}
            </div>

            <div className="space-y-3">
              {filtered.slice(0, 60).map((idx) => (
                <TrendCard
                  key={idx.index_id}
                  index={idx}
                  selected={pickedSet.has(idx.index_id)}
                  captain={captain === idx.index_id}
                  vc={vc === idx.index_id}
                  onClick={() => togglePick(idx)}
                />
              ))}
            </div>

            <button
              onClick={accept}
              disabled={submitting || picks.length !== 6 || !captain || !vc}
              data-testid="duel-accept-submit"
              className="sticky bottom-24 w-full py-3.5 rounded-full font-bold text-black disabled:opacity-40"
              style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)", boxShadow: "0 0 22px rgba(0,240,255,0.35)" }}
            >
              {submitting ? "Locking squad…" : `Accept & Battle (${duel.creator_lineup.points.toFixed(0)} to beat)`}
            </button>
          </>
        )}

        {isDone && (
          <button onClick={() => nav("/duel/new")} data-testid="duel-rematch" className="w-full py-3 rounded-full font-bold text-black" style={{ background: "linear-gradient(90deg,#FF0055,#B026FF)" }}>
            Start a Rematch →
          </button>
        )}

        <div className="h-10" />
      </div>
      <BottomNav />
    </div>
  );
}
